import React, { useCallback, useEffect, useRef, useState } from "react";

import { shallowEqual, useDispatch, useSelector } from "react-redux";

import {
  bulkUpdateBusinesses,
  closeConfirmModal,
  openConfirmModal,
} from "actions";

import { SecondaryButton } from "components/Form";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "components/MaterialDialog";

import { getBusinesses } from "selectors";
import {
  selectAllTemporaryBusinesses,
  selectTemporaryBusinessesForSale,
  selectUsedTemporaryBusinessesInCurrentSale,
} from "selectors/temporaryBusinesses";

import { getLivestockSaleId } from "lib/navigation";

import { TemporaryBusiness } from "components/Business/TemporaryBusiness";

import { useHasBulkUpdateBusinessesPermission } from "hooks";

import { TemporaryBusinessIntro } from "./TemporaryBusinessIntro";
import { TemporaryBusinesses } from "./TemporaryBusinesses";

interface TemporaryBusinessesDialogProps {
  // Without a Sale, covers every Sale.
  livestockSaleId?: number;
  // Just this Business - the dialog closes once it's resolved.
  businessId?: string;
  onClose: () => void;
  // Called instead of onClose when the Business resolves into an active one.
  onResolved?: (resultingBusinessId: string) => void;
}

export function TemporaryBusinessesDialog({
  livestockSaleId,
  businessId,
  onClose,
  onResolved,
}: TemporaryBusinessesDialogProps) {
  const dispatch = useDispatch();
  const temporaryBusinesses = useSelector((state: State) => {
    if (businessId) {
      const business = getBusinesses(state)[businessId];
      return business?.isTemporary ? [business] : [];
    }
    if (!livestockSaleId) {
      return selectAllTemporaryBusinesses(state);
    }
    // Inside the Sale, only the ones used in it. Elsewhere its lots aren't
    // loaded, so usage isn't known.
    return livestockSaleId === getLivestockSaleId()
      ? selectUsedTemporaryBusinessesInCurrentSale(state)
      : selectTemporaryBusinessesForSale(state, livestockSaleId);
  }, shallowEqual);

  const resolvedBusiness = useSelector((state: State) =>
    businessId ? getBusinesses(state)[businessId] : undefined,
  );
  const mergeTargetId = useRef<string>();
  const isResolved = Boolean(businessId) && !temporaryBusinesses.length;
  useEffect(() => {
    if (!isResolved) {
      return;
    }
    // Merged ones leave the store.
    const resultingBusinessId = resolvedBusiness
      ? resolvedBusiness.isActive && resolvedBusiness.id
      : mergeTargetId.current;
    if (resultingBusinessId && onResolved) {
      onResolved(resultingBusinessId);
    } else {
      onClose();
    }
  }, [isResolved, onClose, onResolved, resolvedBusiness]);
  // Which cards currently have a match - the yellow, unmatched ones (no match
  // found, or the match cleared) are what the bulk actions apply to.
  const [isMatchedById, setIsMatchedById] = useState<Record<string, boolean>>(
    {},
  );
  const handleMatchedChange = useCallback(
    (id: string, isMatched: boolean | null) =>
      setIsMatchedById(current => {
        if (isMatched === null) {
          const { [id]: removedIgnored, ...rest } = current;
          return rest;
        }
        return current[id] === isMatched
          ? current
          : { ...current, [id]: isMatched };
      }),
    [],
  );
  const unmatched = temporaryBusinesses.filter(
    business => isMatchedById[business.id] === false,
  );
  const unmatchedCount = unmatched.length;
  const canBulkUpdate = useHasBulkUpdateBusinessesPermission();
  const bulkDisabledReason = canBulkUpdate
    ? undefined
    : "Requires the Bulk Update Businesses permission";
  const noun = unmatchedCount === 1 ? "business" : "businesses";

  const confirmBulk = (
    title: string,
    message: string,
    update: Partial<Business>,
    changeReason: string,
  ) => {
    dispatch(
      openConfirmModal({
        title,
        message,
        actions: [
          {
            label: "Cancel",
            secondary: true,
            onClick: () => dispatch(closeConfirmModal()),
          },
          {
            label: title,
            onClick: () => {
              // One request for them all.
              dispatch(
                bulkUpdateBusinesses(
                  unmatched.map(business => ({ id: business.id, ...update })),
                  {
                    changeReason,
                    suppressToast: false,
                    suppressErrorToast: false,
                    suppressSuccessToast: false,
                  },
                ),
              );
              dispatch(closeConfirmModal());
            },
          },
        ],
      }),
    );
  };

  const handleInactivateUnmatched = () =>
    confirmBulk(
      "Inactivate Unmatched",
      `Inactivate the ${unmatchedCount} unmatched temporary ${noun}? They will ` +
        "no longer need resolving, and won't be visible outside the sale " +
        "they were created in.",
      { isTemporary: false, isActive: false },
      "Archived via Temporary Businesses",
    );

  const handleActivateUnmatched = () =>
    confirmBulk(
      "Activate Unmatched",
      `Activate the ${unmatchedCount} unmatched temporary ${noun} as new, ` +
        "permanent businesses?",
      { isTemporary: false, isActive: true },
      "Resolved via Temporary Businesses",
    );

  return (
    <Dialog open maxWidth="lg" fullWidth onClose={onClose}>
      <DialogTitle onClose={onClose}>
        {businessId
          ? "Temporary Business"
          : livestockSaleId
            ? "Temporary Businesses"
            : "Review Temporary Businesses"}
      </DialogTitle>
      <DialogContent dividers>
        {/* One Business needs no sections or search - just its card. */}
        {businessId ? (
          temporaryBusinesses[0] && (
            <>
              <TemporaryBusinessIntro business={temporaryBusinesses[0]} />
              <TemporaryBusiness
                business={temporaryBusinesses[0]}
                onMerge={target => {
                  mergeTargetId.current = target.id;
                }}
              />
            </>
          )
        ) : (
          <TemporaryBusinesses
            temporaryBusinesses={temporaryBusinesses}
            onMatchedChange={handleMatchedChange}
          />
        )}
      </DialogContent>
      {/* Always shown, so the actions are easy to find. */}
      {!businessId && (
        // Disabled buttons don't fire hover events, so the reason sits on a
        // wrapper.
        <DialogActions title={bulkDisabledReason}>
          <SecondaryButton
            data-tour="inactivate-unmatched"
            disabled={!canBulkUpdate || unmatchedCount === 0}
            onClick={handleInactivateUnmatched}
          >
            {`Inactivate (${unmatchedCount}) Unmatched`}
          </SecondaryButton>
          <SecondaryButton
            data-tour="activate-unmatched"
            disabled={!canBulkUpdate || unmatchedCount === 0}
            onClick={handleActivateUnmatched}
          >
            {`Activate (${unmatchedCount}) Unmatched`}
          </SecondaryButton>
        </DialogActions>
      )}
    </Dialog>
  );
}
